import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import SearchModal from "@/components/SearchModal";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Catalog", to: "/shop" },
  { label: "About Us", to: "/our-story" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 w-full bg-white transition-shadow duration-300 ${
          scrolled ? "shadow-[0_1px_16px_rgba(0,0,0,0.07)]" : ""
        }`}
      >
        <div className="relative mx-auto flex h-20 max-w-[1440px] items-center px-5 sm:px-8 lg:px-12">
          <button
            data-testid="mobile-menu-button"
            className="p-1 text-neutral-900 transition-opacity hover:opacity-60 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="main-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[15px] transition-colors duration-200 ${
                    isActive ? "font-medium text-neutral-900" : "text-neutral-800 hover:text-neutral-500"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/"
            data-testid="brand-logo"
            className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-logo text-xl font-bold uppercase leading-none tracking-[0.045em] text-neutral-900 sm:text-2xl lg:text-[27px]"
          >
            Everyday Essentials
            <sup className="ml-0.5 align-super text-[9px] font-medium tracking-normal">®</sup>
          </Link>

          <div className="ml-auto flex items-center gap-5 sm:gap-6">
            <button
              data-testid="search-open-button"
              className="text-neutral-900 transition-opacity hover:opacity-50"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <Search size={21} strokeWidth={1.5} />
            </button>
            <button
              data-testid="account-button"
              className="hidden text-neutral-900 transition-opacity hover:opacity-50 sm:block"
              onClick={() => navigate("/account")}
              aria-label="Account"
            >
              <User size={21} strokeWidth={1.5} />
            </button>
            <button
              data-testid="cart-open-button"
              className="relative text-neutral-900 transition-opacity hover:opacity-50"
              onClick={() => setOpen(true)}
              aria-label="Cart"
            >
              <ShoppingBag size={21} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-neutral-900 px-1 text-[10px] font-semibold text-white"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="animate-fade-up border-t border-neutral-100 bg-white px-5 py-4 lg:hidden" data-testid="mobile-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="block py-3 text-base text-neutral-900 transition-opacity hover:opacity-60"
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/account"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-nav-account"
              className="block py-3 text-base text-neutral-900 transition-opacity hover:opacity-60"
            >
              Account
            </NavLink>
          </nav>
        )}
      </header>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
